/**
 * Auth Callback Page
 *
 * Handles OAuth callback after successful Google authentication.
 * Extracts token from URL and stores it in localStorage, then redirects.
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

export const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      // Extract parameters from URL
      const token = searchParams.get('token');
      const status = searchParams.get('status');
      const isNewUser = searchParams.get('isNewUser') === 'true';

      // Check for errors
      const error = searchParams.get('error');
      if (error) {
        console.error('OAuth error:', error);
        const message = searchParams.get('message') || 'Authentication failed';
        navigate(`/login?error=${encodeURIComponent(message)}`);
        return;
      }

      // Validate token
      if (!token) {
        console.error('No token in callback URL');
        navigate('/login?error=missing_token');
        return;
      }

      // Store token and fetch user profile
      localStorage.setItem('auth_token', token);

      try {
        // Fetch user profile using the token
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const user = await response.json();

        // Update auth store - WAIT for state to update before navigating
        setUser(user);
        setToken(token);

        // Notify other tabs about successful login via BroadcastChannel
        try {
          if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel('auth_events');
            channel.postMessage({
              type: 'auth:login',
              timestamp: Date.now(),
              userId: user.id,
              source: 'oauth_callback'
            });
            channel.close();
            console.log('[Auth-Hub] Login event broadcasted to other tabs');
          }
        } catch (error) {
          console.error('[Auth-Hub] Failed to broadcast login event:', error);
        }

        // Check if there's a redirect_to URL
        const redirectTo = searchParams.get('redirect_to');

        // Small delay to ensure store updates propagate to all consumers
        // This prevents race condition where ProtectedRoute checks isAuthenticated
        // before the store update completes
        await new Promise(resolve => setTimeout(resolve, 100));

        // Redirect based on user status
        if (status === 'pending') {
          // New user awaiting approval
          navigate('/pending-approval');
        } else if (redirectTo) {
          // Redirect back to the service that initiated login
          window.location.href = `${redirectTo}${redirectTo.includes('?') ? '&' : '?'}token=${token}`;
        } else if (isNewUser) {
          // First time login, show welcome
          navigate('/services?welcome=true');
        } else {
          // Regular login
          navigate('/services');
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
        localStorage.removeItem('auth_token');
        navigate('/login?error=profile_load_failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, setToken]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '20px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>
          Completing sign in...
        </div>
        <div style={{ fontSize: '14px', color: '#5f6368' }}>
          Please wait while we set up your account
        </div>
      </div>
    </div>
  );
};
